import PdmCard from "../../components/PdmCard";
import { BRAND } from "../../brand";
import { DEVICE_TYPES } from "../../domain/models";

const accentColor = BRAND.magenta;

export default function DashboardView({
  devices,
  bmsDevice,
  telemetry,
  onToggle,
  onDim,
}) {
  const bms = bmsDevice;
  const soc = bms?.metrics?.soc ?? null;
  const voltage = bms?.metrics?.voltage ?? null;
  const power = bms?.metrics?.power ?? null;
  const socAngle = soc != null ? (soc / 100) * 360 : 0;

  const fmtVoltage = voltage != null ? `${voltage}V` : "--";
  const fmtPower = power != null ? `${power > 0 ? "+" : ""}${power}W` : "--";

  const quickControls = devices.filter(
    (d) => d.showOnDashboard && d.type === DEVICE_TYPES.PDM_CHANNEL
  );

  const vitalsCards = [
    {
      id: "load",
      title: "Total Load Current",
      value: "11.6A",
      points: "0,30 10,26 20,28 30,22 40,24 50,18 60,22 70,16 80,20 90,14 100,18",
    },
    {
      id: "temp",
      title: "Battery Temperature",
      value: "21°C",
      points: "0,20 10,22 20,18 30,16 40,18 50,20 60,24 70,22 80,25 90,23 100,26",
    },
  ];

  return (
    <div className="space-y-5 pb-6">
      {/* Summary row */}
      <div className="grid grid-cols-3 gap-3 pt-1">
        {/* SoC donut */}
        <div className="flex flex-col items-center justify-between rounded-3xl border border-brand-purple bg-brand-purple px-3 pt-3 pb-4">
          <span className="text-sm font-semibold text-brand-light">SoC</span>
          <div className="mt-2 flex flex-1 items-center justify-center">
            {bms != null ? (
              <div className="relative h-24 w-24">
                <div
                  className="absolute inset-0 rounded-full"
                  style={{
                    backgroundImage: `conic-gradient(${accentColor} 0deg, ${accentColor} ${socAngle}deg, #3a2570 ${socAngle}deg, #3a2570 360deg)`,
                  }}
                />
                <div className="absolute inset-[10px] rounded-full bg-brand-navy border border-brand-purple flex items-center justify-center">
                  <span className="text-2xl font-bold text-brand-light">
                    {soc ?? "--"}%
                  </span>
                </div>
              </div>
            ) : (
              <span className="text-2xl font-bold text-brand-light/30">--</span>
            )}
          </div>
        </div>

        {/* Voltage */}
        <div className="flex flex-col items-center justify-between rounded-3xl border border-brand-purple bg-brand-purple px-3 pt-3 pb-4 text-center">
          <span className="text-sm font-semibold text-brand-light">Voltage</span>
          <div className="flex flex-1 items-center justify-center">
            <span
              className={`text-2xl font-bold ${
                fmtVoltage === "--" ? "text-brand-light/30" : "text-brand-light"
              }`}
            >
              {fmtVoltage}
            </span>
          </div>
        </div>

        {/* Power */}
        <div className="flex flex-col items-center justify-between rounded-3xl border border-brand-purple bg-brand-purple px-3 pt-3 pb-4 text-center">
          <span className="text-sm font-semibold text-brand-light">Power</span>
          <div className="flex flex-1 items-center justify-center">
            <span
              className={`text-xl font-bold leading-tight ${
                power == null
                  ? "text-brand-light/30"
                  : power >= 0
                  ? "text-emerald-400"
                  : "text-red-400"
              }`}
            >
              {fmtPower}
            </span>
          </div>
        </div>
      </div>

      {/* Quick Controls */}
      <section className="space-y-3">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-black italic text-brand-light">Quick Controls</h2>
          <span className="text-xs text-brand-light/40">Instant access</span>
        </div>

        {bms?.showOnDashboard || quickControls.length > 0 ? (
          <div className="space-y-2">
            {/* BMS tile — shown when Show on Dashboard is enabled */}
            {bms?.showOnDashboard && (
              <div className="rounded-3xl border border-brand-navy bg-brand-purple px-4 py-3 flex items-center gap-4">
                <div className="flex h-11 w-11 items-center justify-center rounded-full bg-brand-navy border border-brand-navy">
                  <span className="material-symbols-outlined text-xl text-brand-magenta">battery_charging_full</span>
                </div>
                <div className="flex-1 flex flex-col gap-1.5 min-w-0">
                  <div className="flex items-center justify-between gap-2">
                    <div className="flex flex-col min-w-0">
                      <span className="text-sm font-semibold text-brand-light truncate">{bms.name}</span>
                      <span className="text-[11px] text-emerald-400">
                        {bms.metrics?.statusLine ?? "—"} · {bms.metrics?.voltage != null ? `${bms.metrics.voltage} V` : "—"}
                      </span>
                    </div>
                    <span className="text-2xl font-black italic text-brand-magenta shrink-0">
                      {bms.metrics?.soc ?? "--"}%
                    </span>
                  </div>
                  {/* Charge bar */}
                  <div className="h-1.5 rounded-full bg-brand-navy overflow-hidden">
                    <div
                      className="h-full rounded-full bg-brand-magenta transition-all"
                      style={{ width: `${bms.metrics?.soc ?? 0}%` }}
                    />
                  </div>
                  <div className="flex items-center gap-4 text-[11px] text-brand-light/50">
                    {bms.metrics?.current != null && (
                      <span>{bms.metrics.current > 0 ? "+" : ""}{bms.metrics.current} A</span>
                    )}
                    {bms.metrics?.power != null && (
                      <span>{bms.metrics.power > 0 ? "+" : ""}{bms.metrics.power} W</span>
                    )}
                    {bms.metrics?.timeLeft && (
                      <span className="ml-auto">{bms.metrics.timeLeft}</span>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* PDM channel cards */}
            {quickControls.map((device) => (
              <PdmCard
                key={device.id}
                device={device}
                onToggle={onToggle}
                onDimChange={onDim}
              />
            ))}
          </div>
        ) : (
          <div className="rounded-3xl border border-dashed border-brand-purple bg-brand-navy px-6 py-6 text-center text-xs text-brand-light/50">
            No quick controls configured.
          </div>
        )}
      </section>

      {/* System Vitals */}
      <section className="space-y-3 pb-2">
        <h2 className="text-lg font-black italic text-brand-light">System Vitals</h2>
        <div className="grid grid-cols-2 gap-3">
          {vitalsCards.map((card) => (
            <div
              key={card.id}
              className="rounded-3xl border border-brand-purple bg-brand-purple px-4 py-3 flex flex-col gap-2"
            >
              <div className="flex flex-col gap-1">
                <span className="text-sm font-semibold text-brand-light">{card.title}</span>
                <span className="text-xl font-bold text-brand-light">{card.value}</span>
              </div>
              <div className="mt-1 h-10 w-full">
                <svg viewBox="0 0 100 40" className="h-full w-full" preserveAspectRatio="none">
                  <polyline
                    fill="none"
                    stroke={accentColor}
                    strokeWidth="2.5"
                    strokeLinecap="round"
                    points={card.points}
                  />
                </svg>
              </div>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}
